<template>
  <div id="zone-event-module" class="premium-dashboard">
    <!-- Full Page Refresh Overlay Loader -->
    <v-overlay :value="ProgressLoadingFlag" z-index="999">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          width="6"
          color="white"
        ></v-progress-circular>
        <p class="white--text mt-4 font-weight-bold text-subtitle-1">
          Refreshing Data... Please wait
        </p>
      </div>
    </v-overlay>

    <!-- Background Floating Light Orbs -->
    <div class="glow-orb orb-1"></div>
    <div class="glow-orb orb-2"></div>

    <!-- Massive Hero Dashboard Header -->
    <v-card class="hero-card mb-6" flat>
      <div class="hero-glow-effect"></div>

      <!-- Floating Decorative Ambient Icons -->
      <v-icon class="deco-icon icon-trophy" color="rgba(255, 255, 255, 0.08)"
        >mdi-trophy-outline</v-icon
      >
      <v-icon class="deco-icon icon-ribbon" color="rgba(255, 255, 255, 0.08)"
        >mdi-ribbon</v-icon
      >
      <v-icon class="deco-icon icon-star" color="rgba(255, 255, 255, 0.08)"
        >mdi-star-outline</v-icon
      >
      <v-icon class="deco-icon icon-cert" color="rgba(255, 255, 255, 0.08)"
        >mdi-certificate-outline</v-icon
      >

      <!-- Ocean Wave Background Animation -->
      <div class="waves-container">
        <svg
          class="waves"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          viewBox="0 24 150 28"
          preserveAspectRatio="none"
          shape-rendering="auto"
        >
          <defs>
            <path
              id="gentle-wave"
              d="M-160 44c30 0 58-18 88-18s58 18 88 18 58-18 88-18 58 18 88 18v44h-352z"
            />
          </defs>
          <g class="parallax">
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="0"
              fill="rgba(255, 255, 255, 0.05)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="3"
              fill="rgba(255, 255, 255, 0.08)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="5"
              fill="rgba(255, 255, 255, 0.03)"
            />
            <use
              xlink:href="#gentle-wave"
              x="48"
              y="7"
              fill="rgba(255, 255, 255, 0.07)"
            />
          </g>
        </svg>
      </div>

      <br />
      <v-row class="ma-0 align-center relative-content">
        <v-col cols="12" md="9" class="pa-6 pb-12">
          <div class="d-flex align-center title-container">
            <v-icon large color="white" class="mr-3 title-icon pulse-animation"
              >mdi-trophy-award</v-icon
            >
            <h1 class="hero-title animate-title">{{ PageTitle }}</h1>
          </div>
          <p class="hero-subtitle animate-subtitle">
            {{ PageSubtitle }}
          </p>
        </v-col>
        <v-col
          cols="12"
          md="3"
          class="pa-6 pb-12 text-md-right d-none d-sm-block"
        >
          <div class="d-inline-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  fab
                  small
                  dark
                  color="rgba(255, 255, 255, 0.2)"
                  class="mr-4 hero-refresh-btn elevation-3"
                  :loading="ProgressLoadingFlag || LoadingFlag"
                  v-bind="attrs"
                  v-on="on"
                  @click="refreshPageData"
                >
                  <v-icon color="white">mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Refresh Dashboard Data</span>
            </v-tooltip>

            <div
              class="quick-stat-pill d-inline-flex align-center px-4 py-2 pulse-border"
            >
              <v-icon color="#4caf50" class="mr-2 spin-pulse-icon"
                >mdi-checkbox-marked-circle</v-icon
              >
              <div class="text-left">
                <span class="pill-label">MRF Engine</span>
                <span class="pill-val">Active System</span>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- Summary Statistics Row -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-total"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Total LOs</span>
              <h2 class="stat-value">{{ totalLosCount }}</h2>
              <div
                class="stat-sublabel text-caption primary--text font-weight-bold mt-1 d-flex align-center"
              >
                <v-icon x-small color="#1565C0" class="mr-1">mdi-domain</v-icon>
                <span>Total Organizations Listed</span>
              </div>
            </div>
            <v-avatar color="rgba(21, 101, 192, 0.1)" size="48">
              <v-icon color="#1565C0">mdi-office-building</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-success"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Points Awarded</span>
              <h2 class="stat-value">{{ pointAddedCount }}</h2>
              <div
                class="stat-sublabel text-caption success--text font-weight-bold mt-1 d-flex align-center"
              >
                <v-icon x-small color="#4caf50" class="mr-1"
                  >mdi-check-circle-outline</v-icon
                >
                <span>Efficiency Recorded</span>
              </div>
            </div>
            <v-avatar color="rgba(76, 175, 80, 0.1)" size="48">
              <v-icon color="#4caf50">mdi-star-circle</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-pending"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Pending Points</span>
              <h2 class="stat-value">{{ pendingPointsCount }}</h2>
              <div
                class="stat-sublabel text-caption warning--text font-weight-bold mt-1 d-flex align-center"
              >
                <v-icon x-small color="#ff9800" class="mr-1"
                  >mdi-clock-outline</v-icon
                >
                <span>Eligible for Awarding</span>
              </div>
            </div>
            <v-avatar color="rgba(255, 152, 0, 0.1)" size="48">
              <v-icon color="#ff9800">mdi-file-document-edit-outline</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card">
          <div class="stat-card-glow orb-rev"></div>
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Selected LOs</span>
              <h2 class="stat-value">{{ selectedCheckedCount }}</h2>
              <div
                class="stat-sublabel text-caption info--text font-weight-bold mt-1 d-flex align-center"
              >
                <v-icon x-small color="#1e88e5" class="mr-1"
                  >mdi-checkbox-multiple-marked</v-icon
                >
                <span>Ready to Submit</span>
              </div>
            </div>
            <v-avatar color="rgba(30, 136, 229, 0.1)" size="48">
              <v-icon color="#1e88e5">mdi-format-list-checks</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Section -->
    <v-card class="filter-card glass-card mb-6">
      <div class="filter-card-header d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-filter-variant</v-icon>
        <h3 class="filter-card-title">Search Filters</h3>
      </div>

      <v-card-text class="pa-6">
        <v-form ref="filterForm" v-model="formValid" lazy-validation>
          <v-row>
            <!-- Zone Filter -->
            <v-col cols="12" sm="6" md="4" lg="4" class="py-2">
              <label class="filter-label">Zone *</label>
              <v-autocomplete
                v-model="SelectedZoneId"
                :items="ZoneOptions"
                :loading="ZoneOptionsLoading"
                :disabled="IsZP"
                :rules="[(v) => !!v || 'Zone is required']"
                placeholder="Select Zone"
                dense
                clearable
                outlined
                rounded
                class="premium-select"
                @change="getLomCodeOptions"
              ></v-autocomplete>
            </v-col>

            <!-- LO Filter (Optional) -->
            <v-col cols="12" sm="6" md="4" lg="4" class="py-2">
              <label class="filter-label"
                >Local Organization (LO) (Optional)</label
              >
              <v-autocomplete
                v-model="SelectedLoIds"
                :items="LoOptions"
                :loading="LoOptionsLoading"
                placeholder="Select LO(s) (Optional)"
                multiple
                dense
                clearable
                outlined
                rounded
                class="premium-select"
              ></v-autocomplete>
            </v-col>

            <!-- Efficiency Type Filter -->
            <v-col cols="12" sm="6" md="4" lg="4" class="py-2">
              <label class="filter-label">Efficiency Criteria *</label>
              <v-autocomplete
                v-model="SelectedEfficiencyTypeId"
                :items="EfficiencyTypeOptions"
                :loading="EfficiencyTypeOptionsLoading"
                :rules="[(v) => !!v || 'Efficiency Type is required']"
                placeholder="Select Efficiency Criteria"
                dense
                clearable
                outlined
                rounded
                class="premium-select"
              ></v-autocomplete>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div class="d-flex flex-wrap align-center justify-end">
            <v-btn
              class="filter-btn btn-clear mr-3 px-6"
              rounded
              outlined
              @click="resetFilters"
            >
              <v-icon left small>mdi-refresh</v-icon> Reset Filters
            </v-btn>

            <v-btn
              class="filter-btn btn-search px-8"
              color="primary"
              rounded
              :loading="LoadingFlag"
              @click="searchRecords"
            >
              <v-icon left small>mdi-magnify</v-icon> Search Records
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Instruction Note Before Data Table -->
    <v-alert
      v-if="TableData.length > 0"
      type="info"
      border="left"
      colored-border
      elevation="2"
      class="mb-6 text-instruction-alert rounded-lg"
      icon="mdi-information-outline"
    >
      <strong>Note:</strong> Please select the checkbox next to the Local
      Organizations (LOs) you wish to award points to, then click the
      <strong>Submit Points</strong> button at the bottom of the table to save.
    </v-alert>

    <!-- Data Table Container -->
    <v-card class="glass-card table-container-card" flat>
      <div
        class="pa-6 d-flex flex-wrap align-center justify-space-between border-bottom"
      >
        <div class="d-flex align-center">
          <v-icon color="#1565C0" class="mr-2">mdi-table</v-icon>
          <span class="result-count-text">
            {{ TableData.length }}
            {{ TableData.length === 1 ? "Record" : "Records" }} Found
          </span>
        </div>

        <div class="d-flex align-center flex-wrap">
          <v-text-field
            v-model="searchQuery"
            placeholder="Search LO, President, Membership ID..."
            prepend-inner-icon="mdi-magnify"
            dense
            outlined
            rounded
            hide-details
            class="global-table-search"
          ></v-text-field>
        </div>
      </div>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="TableHeaders"
          :items="TableData"
          :search="searchQuery"
          :loading="LoadingFlag"
          loading-text="Loading LO details... Please wait"
          class="elevation-0 premium-table"
          :items-per-page="15"
        >
          <!-- Select Header Checkbox -->
          <template v-slot:header.select>
            <v-checkbox
              v-model="selectAll"
              @change="toggleSelectAll"
              :disabled="selectableRows.length === 0"
              hide-details
              class="ma-0 pa-0"
            ></v-checkbox>
          </template>

          <!-- Select Row Checkbox -->
          <template v-slot:item.select="{ item }">
            <v-checkbox
              v-model="SelectedCheckedLoIds"
              :value="item.LomId"
              :disabled="item.PointAddedStatus"
              hide-details
              class="ma-0 pa-0"
            ></v-checkbox>
          </template>

          <!-- LO Name Column -->
          <template v-slot:item.LomName="{ item }">
            <span class="member-name">{{ item.LomName }}</span>
          </template>

          <!-- LO Status Column -->
          <template v-slot:item.LomStatusTxt="{ item }">
            <v-chip
              :color="item.LomStatus == 1 ? '#e3f2fd' : '#fff3e0'"
              :text-color="item.LomStatus == 1 ? '#1565c0' : '#e65100'"
              small
              class="status-chip font-weight-bold"
            >
              <v-icon
                left
                x-small
                :color="item.LomStatus == 1 ? '#1565c0' : '#e65100'"
              >
                {{
                  item.LomStatus == 1 ? "mdi-check-decagram" : "mdi-clock-fast"
                }}
              </v-icon>
              {{
                item.LomStatusTxt ||
                (item.LomStatus == 1 ? "Active" : "Provisional")
              }}
            </v-chip>
          </template>

          <!-- President Name & Membership ID Column -->
          <template v-slot:item.PresidentName="{ item }">
            <div class="d-flex flex-column py-1">
              <span class="member-name">{{ item.PresidentName }}</span>
              <span
                v-if="
                  item.PresidentMembershipId &&
                  item.PresidentMembershipId !== 'N/A'
                "
                class="table-date-badge mt-1"
                style="width: fit-content"
              >
                <v-icon x-small left color="#1565c0"
                  >mdi-card-account-details-outline</v-icon
                >
                ID: {{ item.PresidentMembershipId }}
              </span>
            </div>
          </template>

          <!-- Zone Name Column -->
          <template v-slot:item.ZoneName="{ item }">
            <span class="member-name">{{ item.ZoneName }}</span>
          </template>

          <!-- Point Added Status Column -->
          <template v-slot:item.PointAddedStatus="{ item }">
            <v-chip
              v-if="item.PointAddedStatus"
              color="#e8f5e9"
              text-color="#2e7d32"
              small
              class="status-chip font-weight-bold"
            >
              <v-icon left x-small color="#2e7d32">mdi-check-circle</v-icon>
              {{
                item.Points
                  ? item.Points + " Points Added"
                  : "Point Added (+0.5)"
              }}
            </v-chip>
            <v-chip
              v-else
              color="#eceff1"
              text-color="#607d8b"
              small
              class="status-chip font-weight-bold"
            >
              <v-icon left x-small color="#607d8b">mdi-clock-outline</v-icon>
              Not Added
            </v-chip>
          </template>

          <!-- Action Column -->
          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="item.DeleteFlag"
              color="error"
              small
              rounded
              class="elevation-1"
              @click="deletePointRecord(item)"
            >
              <v-icon left small>mdi-delete</v-icon> Delete
            </v-btn>
          </template>

          <!-- Skeleton Loader -->
          <template v-slot:loading>
            <div class="pa-6">
              <v-skeleton-loader type="table-row-divider@6"></v-skeleton-loader>
            </div>
          </template>

          <!-- Empty State -->
          <template v-slot:no-data>
            <div class="py-12 text-center">
              <v-avatar color="rgba(21, 101, 192, 0.08)" size="80" class="mb-4">
                <v-icon size="40" color="#1565c0">mdi-table</v-icon>
              </v-avatar>
              <h3 class="empty-state-title">{{ EmptyStateTitle }}</h3>
              <p class="empty-state-subtitle">
                Try selecting different filter criteria or perform a search to
                load data.
              </p>
            </div>
          </template>
        </v-data-table>

        <!-- Actions Footer After Data Table -->
        <div
          v-if="TableData.length > 0"
          class="pa-6 border-top d-flex flex-wrap align-center justify-space-between bg-footer-actions"
        >
          <div class="d-flex align-center text-subtitle-2 text--secondary">
            <v-icon small color="#1565C0" class="mr-2"
              >mdi-checkbox-marked-outline</v-icon
            >
            <span
              >Selected LOs:
              <strong>{{ SelectedCheckedLoIds.length }}</strong> of
              {{ selectableRows.length }} pending</span
            >
          </div>

          <v-btn
            class="btn-add-payment btn-submit-points px-8"
            color="success"
            rounded
            large
            elevation="3"
            :disabled="SelectedCheckedLoIds.length === 0"
            :loading="SaveLoadingFlag"
            @click="saveSelectedPoints"
          >
            <v-icon left>mdi-check-decagram</v-icon>
            Submit Points ({{ SelectedCheckedLoIds.length }})
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  name: "ZoneEvent",
  mixins: [common],
  data() {
    return {
      formValid: true,
      LoadingFlag: false,
      ProgressLoadingFlag: false,
      SaveLoadingFlag: false,
      SearchPerformed: false,
      dateMenu: false,

      // Role & Session
      RoleId: "",
      AdminFlag: false,
      UserRoleId: null,
      IsZP: false,
      SessionZoneId: null,

      // Filters
      FilterDate: new Date().toISOString().slice(0, 10),
      SelectedZoneId: null,
      SelectedLoIds: [],
      SelectedEfficiencyTypeId: null,

      // Filter Options
      ZoneOptions: [],
      ZoneOptionsLoading: false,
      LoOptions: [],
      LoOptionsLoading: false,
      EfficiencyTypeOptions: [],
      EfficiencyTypeOptionsLoading: false,

      // Table & Selection
      searchQuery: "",
      TableData: [],
      SelectedCheckedLoIds: [],
      selectAll: false,

      TableHeaders: [
        { text: "", value: "select", sortable: false, width: "50px" },
        { text: "LO Status", value: "LomStatusTxt", width: "130px" },
        { text: "LO Name", value: "LomName", width: "240px" },
        { text: "President Details", value: "PresidentName", width: "200px" },
        { text: "Zone", value: "ZoneName", width: "90px" },
        {
          text: "Point Added Status",
          value: "PointAddedStatus",
          width: "170px",
        },
        { text: "Action", value: "actions", sortable: false, width: "110px" },
      ],
    };
  },
  computed: {
    isNationalEventPage() {
      return (
        this.$route &&
        this.$route.path &&
        this.$route.path.includes("national-event")
      );
    },
    PageTitle() {
      return this.isNationalEventPage
        ? "National Event - Efficiency Points"
        : "Zone Event - Efficiency Points";
    },
    PageSubtitle() {
      return this.isNationalEventPage
        ? "Manage National Event efficiency points with an elegant, intelligent and user-friendly dashboard experience."
        : "Manage Zone Event efficiency points with an elegant, intelligent and user-friendly dashboard experience.";
    },
    EmptyStateTitle() {
      return this.isNationalEventPage
        ? "No National Event records found"
        : "No Zone Event records found";
    },
    selectableRows() {
      return this.TableData.filter((item) => !item.PointAddedStatus);
    },
    totalLosCount() {
      return this.TableData.length;
    },
    selectedCheckedCount() {
      return this.SelectedCheckedLoIds.length;
    },
    pointAddedCount() {
      return this.TableData.filter((item) => item.PointAddedStatus).length;
    },
    pendingPointsCount() {
      return this.TableData.filter((item) => !item.PointAddedStatus).length;
    },
  },
  watch: {
    SelectedZoneId: function (val) {
      console.log("watch SelectedZoneId: " + val);
      this.SelectedLoIds = [];
      if (val) {
        this.getLomCodeOptions();
      } else {
        this.LoOptions = [];
      }
    },
    ZoneOptions: function (val) {
      console.log(
        "watch ZoneOptions loaded, total zones: " + (val ? val.length : 0)
      );
      console.log({ ZoneOptions: val });
      this.ZoneOptionsLoading = false;
    },
    LoOptions: function (val) {
      console.log(
        "watch LoOptions loaded, total LOs: " + (val ? val.length : 0)
      );
      this.LoOptionsLoading = false;
    },
    EfficiencyTypeOptions: function (val) {
      console.log(
        "watch EfficiencyTypeOptions loaded, total efficiency types: " +
          (val ? val.length : 0)
      );
      this.EfficiencyTypeOptionsLoading = false;
    },
  },
  mounted() {
    this.checkSessionAndRole();
    this.pageData();
  },
  methods: {
    checkSessionAndRole() {
      console.log("checkSessionAndRole called");
      var RoleId = this.$session.get("RoleId");
      RoleId = RoleId == (null || undefined) ? "" : Number(RoleId);
      this.RoleId = RoleId;

      // Role IDs 1 (Admin), 2 (Super Admin), 4 (Staff) get access to all zones
      this.AdminFlag =
        RoleId === 1 || RoleId === 2 || RoleId === 4 ? true : false;
      this.IsZP = !this.AdminFlag;
      console.log("IsZP=" + this.IsZP + ", AdminFlag=" + this.AdminFlag);

      if (this.IsZP) {
        var zone_id = this.$session.get("ZoneId");
        console.log("Non-admin Session zone_id=" + zone_id);
        if (zone_id) {
          this.SessionZoneId = zone_id;
          this.SelectedZoneId = zone_id;
        }
      }
    },
    pageData() {
      console.log("pageData called");
      this.getZoneCodeOptions();
      this.getEfficiencyIdOptions();
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      this.ZoneOptionsLoading = true;
      var selectbox1_source = "SelectedZoneId";
      var selectbox1_destination = "ZoneOptions";
      var selectbox1_url = "api/zone/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      console.log("Fetching Zone options from: " + selectbox1_url);
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getLomCodeOptions() {
      console.log(
        "getLomCodeOptions called for ZoneId: " + this.SelectedZoneId
      );
      var ZoneCode = this.SelectedZoneId;
      if (ZoneCode) {
        this.LoOptionsLoading = true;
        var selectbox1_source = "SelectedLoIds";
        var selectbox1_destination = "LoOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          ZoneCode: ZoneCode,
        };
        console.log("Fetching LO options from: " + selectbox1_url);
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        this.LoOptions = [];
      }
    },
    getEfficiencyIdOptions() {
      console.log(
        "getEfficiencyIdOptions called, isNationalEventPage=" +
          this.isNationalEventPage
      );
      this.EfficiencyTypeOptionsLoading = true;
      var selectbox1_source = "SelectedEfficiencyTypeId";
      var selectbox1_destination = "EfficiencyTypeOptions";
      var selectbox1_url = "api/zone-event/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
        IsNationalEvent: this.isNationalEventPage,
      };
      console.log("Fetching Efficiency Type options from: " + selectbox1_url);
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.ProgressLoadingFlag = true;
      this.getZoneCodeOptions();
      this.getEfficiencyIdOptions();
      if (this.SelectedZoneId) {
        this.getLomCodeOptions();
      }

      if (this.SelectedEfficiencyTypeId && this.SelectedZoneId) {
        this.searchRecords();
      } else {
        setTimeout(() => {
          this.ProgressLoadingFlag = false;
          this.toast("info", "Filters refreshed.");
        }, 600);
      }
    },
    searchRecords() {
      console.log("searchRecords called");
      if (!this.$refs.filterForm.validate()) {
        this.ProgressLoadingFlag = false;
        return;
      }

      this.LoadingFlag = true;
      this.ProgressLoadingFlag = true;
      this.SearchPerformed = true;
      this.SelectedCheckedLoIds = [];
      this.selectAll = false;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/zone-event/lists";

      var payload = {
        UserInterface: 1,
        ZoneId: this.SelectedZoneId,
        LoIds: this.SelectedLoIds,
        Criteria: this.SelectedEfficiencyTypeId,
      };

      const thisIns = this;
      this.$http({
        url: add_url,
        method: "POST",
        data: payload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then((response) => {
          thisIns.LoadingFlag = false;
          thisIns.ProgressLoadingFlag = false;
          var flag = response.data.flag;
          var output = response.data.output;
          if (flag) {
            if (
              response.data.TableContent &&
              response.data.TableContent.TableHeader &&
              response.data.TableContent.TableHeader.length > 0
            ) {
              thisIns.TableHeaders = response.data.TableContent.TableHeader;
            }
            thisIns.TableData =
              response.data.TableContent && response.data.TableContent.TableData
                ? response.data.TableContent.TableData
                : response.data.records || [];
            thisIns.toast("success", output || "Records loaded successfully.");
          } else {
            thisIns.sweetAlert(
              "error",
              response.data.error || output || "Failed to fetch records.",
              false
            );
          }
        })
        .catch((error) => {
          thisIns.LoadingFlag = false;
          thisIns.ProgressLoadingFlag = false;
          thisIns.sweetAlert(
            "error",
            "An error occurred while fetching data.",
            false
          );
        });
    },
    toggleSelectAll(val) {
      if (val) {
        this.SelectedCheckedLoIds = this.selectableRows.map(
          (item) => item.LomId
        );
      } else {
        this.SelectedCheckedLoIds = [];
      }
    },
    saveSelectedPoints() {
      if (this.SelectedCheckedLoIds.length === 0) {
        this.toast("warning", "Please select at least one LO to award points.");
        return;
      }

      Swal.fire({
        title: "Confirm Award Points",
        text: `Are you sure you want to award efficiency points to ${this.SelectedCheckedLoIds.length} selected LO(s)?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, Save Points",
      }).then((result) => {
        if (result.isConfirmed) {
          this.SaveLoadingFlag = true;
          var server_url = companyConfig.apiURL;
          var token = this.$session.get("token");
          token = token == (null || undefined) ? 0 : token;
          var add_url = server_url + "api/zone-event/store";

          var payload = {
            UserInterface: 1,
            Criteria: this.SelectedEfficiencyTypeId,
            LoIds: this.SelectedCheckedLoIds,
          };

          const thisIns = this;
          this.$http({
            url: add_url,
            method: "POST",
            data: payload,
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
          })
            .then((response) => {
              thisIns.SaveLoadingFlag = false;
              var flag = response.data.flag;
              var output = response.data.output || response.data.success;
              if (flag) {
                thisIns.sweetAlert(
                  "success",
                  output || "Points saved successfully.",
                  false
                );
                thisIns.searchRecords();
              } else {
                thisIns.sweetAlert(
                  "error",
                  response.data.error || output || "Failed to save points.",
                  false
                );
              }
            })
            .catch((error) => {
              thisIns.SaveLoadingFlag = false;
              thisIns.sweetAlert(
                "error",
                "An error occurred while saving.",
                false
              );
            });
        }
      });
    },
    deletePointRecord(item) {
      var lom_name = item.LomName || "";
      var jci_event_id = item.JciEventId;

      Swal.fire({
        title: "Confirm Delete Point",
        text: `Are you sure you want to delete the awarded point for ${lom_name}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, Delete",
      }).then((result) => {
        if (result.isConfirmed) {
          this.ProgressLoadingFlag = true;
          var server_url = companyConfig.apiURL;
          var token = this.$session.get("token");
          token = token == (null || undefined) ? 0 : token;
          var add_url = server_url + "api/zone-event/destroy";

          var payload = {
            UserInterface: 1,
            JciEventId: jci_event_id,
          };

          const thisIns = this;
          this.$http({
            url: add_url,
            method: "POST",
            data: payload,
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + token,
            },
          })
            .then((response) => {
              thisIns.ProgressLoadingFlag = false;
              var flag = response.data.flag;
              var output = response.data.output || response.data.success;
              if (flag) {
                thisIns.sweetAlert(
                  "success",
                  output || "Record deleted successfully.",
                  false
                );
                thisIns.searchRecords();
              } else {
                thisIns.sweetAlert(
                  "error",
                  response.data.error || output || "Failed to delete record.",
                  false
                );
              }
            })
            .catch((error) => {
              thisIns.ProgressLoadingFlag = false;
              thisIns.sweetAlert(
                "error",
                "An error occurred while deleting.",
                false
              );
            });
        }
      });
    },
    resetFilters() {
      if (!this.IsZP) {
        this.SelectedZoneId = null;
        this.LoOptions = [];
      } else if (this.SessionZoneId) {
        this.SelectedZoneId = this.SessionZoneId;
      }
      this.SelectedLoIds = [];
      this.SelectedEfficiencyTypeId = null;
      this.TableData = [];
      this.SearchPerformed = false;
      this.SelectedCheckedLoIds = [];
      this.selectAll = false;
      if (this.$refs.filterForm) {
        this.$refs.filterForm.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

.premium-dashboard {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  background-color: #f6f8fc;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding: 12px;
}

/* Floating light ambient background orbs */
.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  pointer-events: none;
  opacity: 0.45;
  z-index: 0;
}

.orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #42a5f5 0%, rgba(21, 101, 192, 0) 70%);
  top: -100px;
  right: -50px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(
    circle,
    rgba(21, 101, 192, 0.2) 0%,
    rgba(66, 165, 245, 0) 70%
  );
  bottom: -150px;
  left: -100px;
}

/* Massive Hero Dashboard Header styling */
.hero-card {
  background: linear-gradient(
    135deg,
    #1565c0 0%,
    #1a73e8 45%,
    #42a5f5 100%
  ) !important;
  border-radius: 20px !important;
  color: white;
  box-shadow: 0 16px 40px rgba(21, 101, 192, 0.25) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.8px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.95;
  margin-top: 6px;
  max-width: 100%;
  white-space: nowrap;
  font-weight: 400;
}

.quick-stat-pill {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.pill-label {
  display: block;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
}

.hero-refresh-btn {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.hero-refresh-btn:hover {
  transform: rotate(180deg) scale(1.12);
  background-color: rgba(255, 255, 255, 0.35) !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2) !important;
}

.pill-val {
  display: block;
  font-size: 13px;
  font-weight: 700;
}

/* Glassmorphism General Cards styling */
.glass-card {
  background: rgba(255, 255, 255, 0.82) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.7) !important;
  border-radius: 20px !important;
  box-shadow: 0 10px 30px rgba(33, 33, 33, 0.04) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* Summary Statistics Cards */
.stat-card {
  padding: 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(21, 101, 192, 0.12) !important;
}

.stat-card-glow {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  filter: blur(40px);
  top: -40px;
  right: -40px;
  opacity: 0.15;
}

.orb-total {
  background-color: #1565c0;
}
.orb-rev {
  background-color: #1e88e5;
}
.orb-success {
  background-color: #4caf50;
}
.orb-pending {
  background-color: #ff9800;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: #78909c;
  letter-spacing: 0.8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 800;
  color: #263238;
  margin-top: 4px;
  letter-spacing: -0.5px;
}

/* Advanced Filter Card header gradient */
.filter-card-header {
  background: linear-gradient(90deg, #1565c0 0%, #1e88e5 100%);
  padding: 16px 24px;
  color: white;
}

.filter-card-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 0;
}

.filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #455a64;
  margin-bottom: 6px;
  display: block;
}

/* Autocomplete/Select box rounded controls */
.premium-select >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.1) !important;
  background-color: white !important;
  transition: all 0.3s ease !important;
}

.premium-select >>> .v-input__control > .v-input__slot:hover {
  border-color: rgba(21, 101, 192, 0.35) !important;
}

.premium-select.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #1565c0 !important;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.15) !important;
}

/* Filter action buttons */
.filter-btn {
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.2px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.btn-clear {
  color: #455a64 !important;
  border: 1.5px solid rgba(69, 90, 100, 0.2) !important;
}

.btn-clear:hover {
  background-color: rgba(69, 90, 100, 0.05) !important;
  border-color: #455a64 !important;
}

.btn-search {
  background: linear-gradient(135deg, #1565c0 0%, #1e88e5 100%) !important;
  box-shadow: 0 6px 20px rgba(21, 101, 192, 0.3) !important;
  border: none !important;
}

.btn-search:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(21, 101, 192, 0.45) !important;
}

.btn-add-payment {
  background: linear-gradient(135deg, #2e7d32 0%, #4caf50 100%) !important;
  box-shadow: 0 6px 20px rgba(46, 125, 50, 0.3) !important;
  border: none !important;
  font-weight: 700 !important;
  text-transform: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.btn-add-payment:hover {
  transform: scale(1.04) translateY(-2px);
  box-shadow: 0 10px 25px rgba(46, 125, 50, 0.5) !important;
}

.text-instruction-alert {
  background: rgba(21, 101, 192, 0.06) !important;
  border-left: 4px solid #1565c0 !important;
  color: #1a237e !important;
  font-size: 15px;
}

.bg-footer-actions {
  background: #f8fafc;
  border-bottom-left-radius: 20px;
  border-bottom-right-radius: 20px;
}

.btn-submit-points {
  font-weight: 700 !important;
  letter-spacing: 0.5px;
}

/* Skeleton Loaders and Empty States styling */
.empty-state-title {
  font-size: 18px;
  font-weight: 700;
  color: #37474f;
  margin-top: 16px;
}

.empty-state-subtitle {
  font-size: 13px;
  color: #78909c;
  max-width: 420px;
  margin: 6px auto 0;
}

/* Data grid container and table styles */
.table-container-card {
  overflow: hidden;
}

.result-count-text {
  font-size: 16px;
  font-weight: 700;
  color: #263238;
}

/* Premium global table search text field */
.global-table-search {
  max-width: 320px;
}

.global-table-search >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(21, 101, 192, 0.12) !important;
  background-color: rgba(21, 101, 192, 0.01) !important;
}

.global-table-search.v-input--is-focused
  >>> .v-input__control
  > .v-input__slot {
  border-color: #1565c0 !important;
  box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.12) !important;
  background-color: white !important;
}

/* Real premium tables */
.premium-table >>> table {
  border-collapse: separate;
  border-spacing: 0;
}

.premium-table >>> thead > tr > th {
  background-color: rgba(21, 101, 192, 0.04) !important;
  color: #1565c0 !important;
  font-weight: 800 !important;
  font-size: 16px !important;
  border-bottom: 2px solid rgba(21, 101, 192, 0.08) !important;
  padding: 16px 18px !important;
}

.premium-table >>> tbody > tr {
  transition: background-color 0.2s ease;
}

.premium-table >>> tbody > tr:hover {
  background-color: rgba(21, 101, 192, 0.03) !important;
}

.premium-table >>> tbody > tr > td {
  border-bottom: 1px solid rgba(21, 101, 192, 0.04) !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  color: #263238;
  padding: 16px 18px !important;
}

/* Badges inside cells */
.table-date-badge {
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  color: #1565c0;
  background: rgba(21, 101, 192, 0.08);
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-family: "Courier New", Courier, monospace;
}

.payment-id-tag {
  font-family: "Courier New", Courier, monospace;
  font-weight: 700;
  font-size: 15px;
  color: #546e7a;
  background: rgba(84, 110, 122, 0.08);
  padding: 4px 10px;
  border-radius: 8px;
}

.member-name {
  font-weight: 700;
  font-size: 16px;
  color: #263238;
  white-space: normal;
  word-break: break-word;
  max-width: 250px;
  display: inline-block;
}

.status-chip {
  font-weight: bold;
}

/* Ocean Wave Styling */
.waves-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  min-height: 40px;
  max-height: 60px;
  z-index: 1;
}

.waves {
  position: relative;
  width: 100%;
  height: 40px;
  margin-bottom: -7px;
}

.parallax > use {
  animation: move-forever 25s cubic-bezier(0.55, 0.5, 0.45, 0.5) infinite;
}

.parallax > use:nth-child(1) {
  animation-delay: -2s;
  animation-duration: 7s;
}

.parallax > use:nth-child(2) {
  animation-delay: -3s;
  animation-duration: 10s;
}

.parallax > use:nth-child(3) {
  animation-delay: -4s;
  animation-duration: 13s;
}

.parallax > use:nth-child(4) {
  animation-delay: -5s;
  animation-duration: 20s;
}

@keyframes move-forever {
  0% {
    transform: translate3d(-90px, 0, 0);
  }
  100% {
    transform: translate3d(85px, 0, 0);
  }
}

/* Hero header animations & shapes */
.hero-glow-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  z-index: 0;
  pointer-events: none;
}

.hero-glow-effect::after {
  content: "";
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.15) 30%,
    rgba(255, 255, 255, 0) 60%
  );
  transform: skewX(-25deg);
  animation: shineSweep 6s infinite ease-in-out;
}

.relative-content {
  position: relative;
  z-index: 2;
}

/* Decorative background icons */
.deco-icon {
  position: absolute !important;
  z-index: 1;
  pointer-events: none;
}

.icon-trophy {
  font-size: 110px !important;
  right: 18%;
  top: -20px;
  transform: rotate(15deg);
  animation: floatDeco 12s infinite ease-in-out;
}

.icon-ribbon {
  font-size: 80px !important;
  left: 10%;
  bottom: -15px;
  transform: rotate(-10deg);
  animation: floatDeco 15s infinite ease-in-out alternate;
}

.icon-star {
  font-size: 90px !important;
  right: 5%;
  bottom: -10px;
  transform: rotate(20deg);
  animation: floatDeco 10s infinite ease-in-out;
}

.icon-cert {
  font-size: 70px !important;
  left: 45%;
  top: -30px;
  opacity: 0.8;
  animation: floatDeco 14s infinite ease-in-out alternate;
}

/* Specific text and icon animations */
.animate-title {
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-subtitle {
  opacity: 0;
  animation: slideInLeft 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.15s forwards;
}

.pulse-animation {
  animation: pulseIcon 3s infinite ease-in-out;
}

.spin-pulse-icon {
  animation: pulseGlowIcon 2s infinite ease-in-out;
}

.pulse-border {
  animation: borderGlow 4s infinite ease-in-out;
}

/* Keyframes definitions */
@keyframes shineSweep {
  0% {
    left: -120%;
  }
  15%,
  100% {
    left: 120%;
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes floatDeco {
  0%,
  100% {
    transform: translateY(0) rotate(15deg) scale(1);
  }
  50% {
    transform: translateY(-12px) rotate(20deg) scale(1.03);
  }
}

@keyframes pulseIcon {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0px rgba(255, 255, 255, 0));
  }
  50% {
    transform: scale(1.08);
    filter: drop-shadow(0 0 10px rgba(255, 255, 255, 0.6));
  }
}

@keyframes pulseGlowIcon {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

@keyframes borderGlow {
  0%,
  100% {
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  }
  50% {
    border-color: rgba(255, 255, 255, 0.45);
    box-shadow: 0 4px 25px rgba(255, 255, 255, 0.15);
  }
}
</style>
